import React, { useEffect, useState, useCallback } from 'react';
import { Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { Alert } from 'antd';
import Main from './pages/Main/Main';
import InnerPage from './pages/InnerPage/InnerPage';
import PageNotFound from '../PageNotFound/container';
import useFilterState from './hooks/useFilterState';
import useSetDefaultFilter from './hooks/useSetDefaultFilter';
import {
  setMainSearchParams,
  setIsCounting,
  resetRequestsData,
  setStatisticsData,
  setDashboardData,
  setDashboardFilteredRequests,
  setDashboardItems,
} from './store/actions/actions';
import { filterNames } from './constants/filterNames';
import { filtersData } from './constants/filtersData';
import RequestsContainer from './components/RequestsContainer/RequestsContainer';
import { getSearchParamsObj } from './helpers/searchParamsFns';
import { statisticsPages } from '../../constants/statisticsPages';
import ExecuteTimeContainer from '../set-execute-time-panel.container';
import WarrantyInfoContainer from '../warranty-info-form.container';
import NewDocumentContainer from '../new-wdocument.container';
import AddressPanelContainer from '../set-address-panel.container';
import ServiceContainer from '../set-service-panel.container';
import MasterContainer from '../set-master-panel.container';
import ExecuterContainer from '../set-executer-panel.container';
import useChangesMethodsForModals from './hooks/useChangesMethodsForModals';
import useRequestCardMutationFns from '../../hooks/useRequestCardMutationFns';
import useAddWarrantyDocsMutation from './mutations/useAddWarrantyDocs';
import MainLoader from './components/MainLoader/MainLoader';
import FiltersBlock from './components/FiltersBlock/FiltersBlock';
import InnerPageHeader from './components/InnerPageHeader/InnerPageHeader';
import { getPageRequests } from './helpers/getPageRequests';
import { loadingStates } from './constants/common';
import { getStatisticsData } from './helpers/getStatisticsData';
import AcceptanceRequestToCalendar from '../../components/AcceptanceRequestToCalendar/AcceptanceRequestToCalendar';
import { getRequestsKeys } from './queries/queryKeys';
import useSetRefreshFn from '../../hooks/useSetRefreshFn';
import { autoUpdatePeriods } from './constants/autoUpdatePeriods';
import styles from './Statistics.module.scss';
import { SwitchStatistic } from './components/SwitchStatistic/SwitchStatistic';
import { ReportInnerPage } from './pages/ReportInnerPage/ReportInnerPage';
import { requestActionNames } from './constants/common';
import useDasboard from './queries/useDashboard';
import useBuildReportMutation from './mutations/useDashboardBuildReport';
import { dashboardRequestsSorter } from './helpers/dashboardRequestsSorter';
import { dashboardRequestsProcessor } from './helpers/dashboardRequestsProcessor';
import { dashboardDataReportTransformer } from './helpers/dashboardDataReportTransformer';
import { dashboardRequestsFilter } from './helpers/dashboardRequestsFilter';

const Statistics = () => {
  const queryClient = useQueryClient();
  const match = useRouteMatch();
  const location = useLocation();
  const dispatch = useDispatch();

  const [initPagesRequests, setInitPagesRequests] = useState(null);
  const [showLongDataAlert, setShowLongDataAlert] = useState(false);
  const [loadingState, setLoadingState] = useState(loadingStates.loaded);
  const [pageTitle, setPageTitle] = useState('');
  const [autoUpdate, setAutoUpdate] = useState(false);
  const [autoUpdatePeriod, setAutoUpdatePeriod] = useState(
    autoUpdatePeriods[15].key
  );
  const [showRequests, setShowRequests] = useState(true);
  const [onlyThisPeriod, setOnlyThisPeriod] = useState(true);
  const [showInLifeCirle, setShowInLifeCirle] = useState(false);
  const [localOnlyThisPeriod, setLocalOnlyThisPeriod] = useState(true);
  const [initDashboardRequestList, setInitDashboardRequestList] =
    useState(null);
  const filteredDashboardRequests = useSelector(
    (state) => state.statisticsStore.filteredDashboardRequests
  );

  const isCounting = useSelector((state) => state.statisticsStore.isCounting);

  const filteredRequests = useSelector(
    (state) => state.statisticsStore.filteredRequests
  );

  const params = new URLSearchParams(location.search);
  const createDateRange = params.get('createDate');

  const handleOnlyThisPeriod = () => {
    dispatch(setIsCounting(true));
    setOnlyThisPeriod((prev) => !prev);

    const timeoutId = setTimeout(() => {
      setLocalOnlyThisPeriod((prev) => !prev);
      dispatch(setIsCounting(false));
    }, 1000);

    return () => {
      clearTimeout(timeoutId);
    };
  };

  const handleShowInLifeCirle = () => setShowInLifeCirle((prev) => !prev);

  const { mutateAsync: addWarrantyDocs } = useAddWarrantyDocsMutation({
    onSuccess: (_data, { requestId }) =>
      queryClient.invalidateQueries(['warrantyDocs', Number(requestId)]),
  });

  const handleAddWarrantyDocs = useCallback(
    (requestId, values) => addWarrantyDocs({ requestId, values }),
    [addWarrantyDocs]
  );

  const handleLongDataAlertClose = () => {
    setShowLongDataAlert(false);
  };

  const handleAutoUpdateChange = (checked) => setAutoUpdate(checked);
  const handleAutoUpdatePeriodClick = ({ key: periodKey }) =>
    setAutoUpdatePeriod(autoUpdatePeriods[periodKey].key);

  const [[fromDate, toDate]] = useFilterState(
    filtersData[filterNames.createDate]
  );

  const { data: dashboardRequests, isLoading: isDashboardLoading } =
    useDasboard(
      fromDate,
      toDate,
      false,
      autoUpdate
        ? {
            refetchInterval: 1000 * 60 * autoUpdatePeriod,
            refetchIntervalInBackground: true,
          }
        : {}
    );

  const { mutate: buildReport, isLoading: isBuildReportLoading } =
    useBuildReportMutation();

  useSetDefaultFilter();

  useSetRefreshFn(
    () => queryClient.invalidateQueries(getRequestsKeys(fromDate, toDate)),
    [fromDate, toDate]
  );

  const { handleWarrantyInfoChange } = useRequestCardMutationFns;

  const handleAfterAcceptanceToCalendarSubmit = ({ updatedRequest }) => {
    if (updatedRequest) {
      queryClient.setQueryData(
        getRequestsKeys(fromDate, toDate),
        (oldRequests) => {
          return oldRequests.map((oldRequest) =>
            oldRequest.id === updatedRequest.id ? updatedRequest : oldRequest
          );
        }
      );
    }
  };

  // Установить начальные заявки для каждой страницы
  useEffect(() => {
    const reportPages = {
      complete: 'complete',
      receive: 'receive',
      annulled: 'annulled',
      period: 'period',
      report: 'report',
    };

    const page = location.pathname.split('/').pop();

    if (
      !reportPages[page] &&
      dashboardRequests &&
      dashboardRequests !== 'Error'
    ) {
      const dashboardItems = dashboardRequestsSorter(
        dashboardRequests,
        localOnlyThisPeriod,
        createDateRange
      );
      const items = [...new Set(Object.values(dashboardItems).flat())];
      const newInitPagesRequests = getPageRequests(items);
      setInitPagesRequests(newInitPagesRequests);
    } else if (
      reportPages[page] &&
      dashboardRequests &&
      dashboardRequests !== 'Error'
    ) {
      const dashboardItems = dashboardRequestsSorter(
        dashboardRequests,
        localOnlyThisPeriod,
        createDateRange
      );

      const dashboardData = Object.entries(dashboardItems).reduce(
        (acc, [key, value]) => {
          acc[key] = dashboardRequestsProcessor(value, createDateRange, key);
          return acc;
        },
        {}
      );

      dispatch(setDashboardItems(dashboardItems));
      dispatch(setDashboardData(dashboardData));
      dispatch(setDashboardFilteredRequests(dashboardItems));
      setInitDashboardRequestList(dashboardItems);
    }

    return () => {
      setInitPagesRequests(null);
    };
  }, [
    dashboardRequests,
    dispatch,
    localOnlyThisPeriod,
    location.pathname,
    location.search,
  ]);

  // Установить необходимые значения для статистики
  useEffect(() => {
    let statisticDataTimer = null;

    const reportPages = {
      complete: 'complete',
      receive: 'receive',
      annulled: 'annulled',
      period: 'period',
      report: 'report',
    };

    const pathPage = location.pathname.split('/').pop();
    const filterValues = getSearchParamsObj(location.search, filtersData);
    const page = match.params.page;

    if (!reportPages[pathPage] && initPagesRequests) {
      dispatch(resetRequestsData());
      dispatch(setIsCounting(true));
      const initRequests = initPagesRequests[page] || initPagesRequests.all;

      statisticDataTimer = setTimeout(() => {
        const statisticData = getStatisticsData({
          requests: initRequests,
          filterValues,
          page,
        });

        dispatch(setStatisticsData(statisticData));
        dispatch(setIsCounting(false));
      }, 0);
    } else if (reportPages[pathPage] && initDashboardRequestList) {
      dispatch(resetRequestsData());
      dispatch(setIsCounting(true));

      const initRequests =
        pathPage === 'report'
          ? Object.values(initDashboardRequestList).flat()
          : initDashboardRequestList[reportPages[pathPage]];

      statisticDataTimer = setTimeout(() => {
        const statisticData = getStatisticsData({
          requests: [...new Set(initRequests)],
          filterValues,
          page,
        });

        dispatch(setStatisticsData(statisticData));
        dispatch(setIsCounting(false));
      }, 0);
    } else {
      dispatch(resetRequestsData());
      clearTimeout(statisticDataTimer);
    }

    return () => {
      clearTimeout(statisticDataTimer);
    };
  }, [
    initPagesRequests,
    location.search,
    match.params.page,
    dispatch,
    location.pathname,
    initDashboardRequestList,
    filteredDashboardRequests,
  ]);

  // Сбросить mainSearchParam
  useEffect(() => {
    return () => {
      dispatch(setMainSearchParams(''));
    };
  }, [dispatch]);

  // Запуск и обнуление таймеров для настройки состояния загрузки
  useEffect(() => {
    let longLoadingTimer = null;
    let noResultTimer = null;

    if (isDashboardLoading) {
      longLoadingTimer = setTimeout(
        () => setLoadingState(loadingStates.longLoading),
        1000 * 10
      );
      noResultTimer = setTimeout(
        () => setLoadingState(loadingStates.noResult),
        1000 * 60 * 2
      );
    } else {
      clearTimeout(longLoadingTimer);
      clearTimeout(noResultTimer);
      setLoadingState(loadingStates.loaded);
    }

    return () => {
      clearTimeout(longLoadingTimer);
      clearTimeout(noResultTimer);
    };
  }, [isDashboardLoading]);

  // Отменяем запрос на заявки, если за 2 минуты они не загрузились
  useEffect(() => {
    if (loadingState === loadingStates.noResult) {
      queryClient.cancelQueries([`requests`, fromDate, toDate]);
      setShowLongDataAlert(true);
    }
  }, [loadingState, fromDate, toDate, queryClient]);

  // Устанавливаем заголовок страницы
  useEffect(() => {
    let title = '';

    switch (match.params.page) {
      case statisticsPages.active:
        title = 'Новые';
        break;
      case statisticsPages.chargeable:
        title = 'Платные';
        break;
      case statisticsPages.expired:
        title = 'Просроченные';
        break;
      case statisticsPages.immediate:
        title = 'Аварийные';
        break;
      case statisticsPages.retry:
        title = 'Открытые повторно';
        break;
      case statisticsPages.lifetime:
        title = 'Статистика';
        break;
      case statisticsPages.all:
        title = 'Всего';
        break;
      default:
        title = '';
    }

    setPageTitle(title);
  }, [match.params.page]);

  useEffect(() => {
    return () => queryClient.removeQueries(getRequestsKeys(fromDate, toDate));
  }, [fromDate, queryClient, toDate]);

  const {
    changeAddress,
    changeService,
    changeMaster,
    changeExecuter,
    changeExecuteTime,
  } = useChangesMethodsForModals();

  const filtersProps = {
    showInLifeCirle,
  };

  const handleActionsMenuClick = (key) => {
    if (key === requestActionNames.excel && filteredRequests) {
      const dashboardReqs = dashboardRequestsFilter(filteredRequests);
      const dashboardItems = dashboardRequestsSorter(
        dashboardReqs,
        localOnlyThisPeriod,
        createDateRange
      );
      const type = location.pathname.split('/').pop();
      let dashboards = null;

      if (type) {
        dashboards = dashboardDataReportTransformer(dashboardItems, type);
      }

      if (dashboards && !!dashboards.length) {
        const reportParams = {
          dashboards,
          onlySelectedPeriod: onlyThisPeriod,
          from: fromDate,
          to: toDate,
        };

        buildReport(reportParams, {
          onSuccess: (response) => {
            if (response) {
              const byteCharacters = atob(response);
              const byteNumbers = new Uint8Array(byteCharacters.length);
              for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
              }
              const blob = new Blob([byteNumbers], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              });

              const url = window.URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', 'report.xlsx');
              document.body.appendChild(link);
              link.click();
              link.remove();
              window.URL.revokeObjectURL(url);
            }
          },
          onError: (error) => {
            console.error('Ошибка при формировании отчета:', error);
          },
        });
      }
    } else {
      console.log('err');
      return;
    }
  };

  const getFiltersProps = (location) => {
    if (location.pathname.split('/').pop() === 'report') {
      return {
        ...filtersProps,
        onlyThisPeriod,
        handleOnlyThisPeriod,
        handleActionsMenuClick,
        isReport: true,
      };
    }

    return filtersProps;
  };

  const reportInnerPageFiltersProps = {
    ...filtersProps,
    handleActionsMenuClick,
    isReport: true,
  };

  return (
    <section className={styles.root}>
      <MainLoader
        loadingState={loadingState}
        loading={isDashboardLoading || isBuildReportLoading || isCounting}
      >
        {showLongDataAlert && (
          <Alert
            style={{ marginTop: '20px' }}
            message="Слишком большой объем данных"
            description="Не удалось загрузить данные. Обновите страницу или выберите меньший период в 'Дате создания'"
            type="error"
            showIcon
            closable
            onClose={handleLongDataAlertClose}
          />
        )}
        <div
          className={
            match.params.page ? styles.innerFirstScreen : styles.mainFirstScreen
          }
        >
          {match.params.page && <InnerPageHeader title={pageTitle} />}
          <Switch>
            <Route
              exact
              path={`${match.path}/:page`}
            >
              <div
                className={
                  match.params.page
                    ? styles.innerFiltersWrap
                    : styles.mainFiltersWrap
                }
              >
                {location.pathname.split('/').pop() === 'report' && (
                  <SwitchStatistic
                    autoUpdate={autoUpdate}
                    handleAutoUpdateChange={handleAutoUpdateChange}
                    autoUpdatePeriod={autoUpdatePeriod}
                    handleAutoUpdatePeriodClick={handleAutoUpdatePeriodClick}
                    onlyThisPeriod={onlyThisPeriod}
                    handleOnlyThisPeriod={handleOnlyThisPeriod}
                    showInLifeCirle={showInLifeCirle}
                    handleShowInLifeCirle={handleShowInLifeCirle}
                  />
                )}
                <FiltersBlock {...getFiltersProps(location)} />
              </div>
              <InnerPage
                onlyThisPeriod={onlyThisPeriod}
                chartTitle={pageTitle}
              />
            </Route>
            <Route
              exact
              path={`${match.path}`}
            >
              <div
                className={
                  match.params.page
                    ? styles.innerFiltersWrap
                    : styles.mainFiltersWrap
                }
              >
                <SwitchStatistic
                  autoUpdate={autoUpdate}
                  handleAutoUpdateChange={handleAutoUpdateChange}
                  autoUpdatePeriod={autoUpdatePeriod}
                  handleAutoUpdatePeriodClick={handleAutoUpdatePeriodClick}
                  onlyThisPeriod={onlyThisPeriod}
                  handleOnlyThisPeriod={handleOnlyThisPeriod}
                  showInLifeCirle={showInLifeCirle}
                  handleShowInLifeCirle={handleShowInLifeCirle}
                />
                <FiltersBlock {...filtersProps} />
              </div>
              <Main />
            </Route>
            <Route
              exact
              path={`${match.path}/report/:page`}
            >
              <ReportInnerPage
                FiltersBlock={
                  <div
                    className={
                      match.params.page
                        ? styles.innerFiltersWrap
                        : styles.mainFiltersWrap
                    }
                  >
                    <FiltersBlock {...reportInnerPageFiltersProps} />
                  </div>
                }
              />
            </Route>
            <Route path="*">
              <PageNotFound />
            </Route>
          </Switch>
          {!match.params.page && <div className={styles.divider} />}
        </div>
        <RequestsContainer
          tableClassname={styles.tableRequestContainer}
          showRequests={showRequests}
          setShowRequests={setShowRequests}
          actionsDropdownClassName={styles.actionsDropdown}
          customPageSize
          rootClassName={styles.requestsContainer}
          showInLifeCirle={showInLifeCirle}
        />
        <ExecuteTimeContainer updateExecuteTime={changeExecuteTime} />
        <WarrantyInfoContainer
          onWarrantyInfoChange={handleWarrantyInfoChange}
        />
        <NewDocumentContainer onAddDocument={handleAddWarrantyDocs} />
        <AddressPanelContainer updateAddress={changeAddress} />
        <ServiceContainer updateService={changeService} />
        <MasterContainer updateMaster={changeMaster} />
        <ExecuterContainer updateExecuter={changeExecuter} />
        <AcceptanceRequestToCalendar
          afterSubmit={handleAfterAcceptanceToCalendarSubmit}
        />
      </MainLoader>
    </section>
  );
};

export default Statistics;
